import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminLeads, getAdminStats } from "../lib/api";
import { LogoMark } from "../components/Logo";
import { computeReport } from "../lib/scoring";
import Report from "../components/tcc/Report";
import {
  LogOut, RefreshCw, Users, PhoneCall, Mail, ClipboardList, Inbox, Download, Gauge, MessageSquare, Eye, X,
} from "lucide-react";

const TABS = [
  { key: "navigator_results", label: "Cyber Risk Navigator", icon: Gauge },
  { key: "consultations", label: "Consultations", icon: Users },
  { key: "callbacks", label: "Callbacks", icon: PhoneCall },
  { key: "contacts", label: "Contact Messages", icon: Inbox },
  { key: "checklists", label: "Checklist Downloads", icon: ClipboardList },
  { key: "profile_downloads", label: "Profile Downloads", icon: Download },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "chat_sessions", label: "AI Chat", icon: MessageSquare },
];

const columnsFor = (key) => {
  switch (key) {
    case "navigator_results":
      return ["created_at", "name", "company", "email", "mobile", "sector", "overall", "tier_label"];
    case "consultations":
      return ["created_at", "name", "company", "email", "phone", "topic", "preferred_date", "preferred_time"];
    case "callbacks":
      return ["created_at", "name", "company", "email", "phone", "best_time"];
    case "contacts":
      return ["created_at", "name", "company", "email", "phone", "subject", "message"];
    case "checklists":
    case "profile_downloads":
      return ["created_at", "name", "company", "email", "phone"];
    case "newsletter":
      return ["created_at", "email"];
    case "chat_sessions":
      return ["created_at", "session_id", "role", "content"];
    default:
      return ["created_at", "name", "email"];
  }
};

function cellValue(row, col) {
  if (col === "sector") return row.profile?.A1 || "—";
  if (col === "created_at") return new Date(row.created_at).toLocaleString("en-IN");
  const v = row[col];
  if (v == null || v === "") return "—";
  return String(v).length > 140 ? String(v).slice(0, 140) + "…" : String(v);
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("tm_admin_token");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [active, setActive] = useState("navigator_results");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportRow, setReportRow] = useState(null); // navigator_results row being viewed

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [s, l] = await Promise.all([getAdminStats(token), getAdminLeads(token)]);
      setStats(s.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("tm_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => leads?.[active] || [], [leads, active]);
  const cols = columnsFor(active);
  const showReportAction = active === "navigator_results";
  const allCols = showReportAction ? [...cols, "report"] : cols;

  const exportCsv = () => {
    if (!rows.length) return;
    const header = cols.join(",");
    const body = rows.map((r) => cols.map((c) => `"${String(cellValue(r, c)).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `techmate-${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className={`sticky top-0 z-20 bg-[#0C2136] text-white px-4 sm:px-8 py-4 flex items-center justify-between ${reportRow ? "print-hide" : ""}`}>
        <div className="flex items-center gap-3">
          <LogoMark size={36} />
          <div>
            <div className="font-display font-black text-lg tracking-tight">TECHMATE Admin</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Leads &amp; Assessments</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 text-xs font-semibold hover:bg-white/10">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={() => { localStorage.removeItem("tm_admin_token"); navigate("/admin/login", { replace: true }); }}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-xs font-semibold"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className={`max-w-7xl mx-auto px-4 sm:px-8 py-8 ${reportRow ? "hidden" : ""}`}>
        {/* stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`admin-stat-${t.key}`}
                className={`rounded-2xl border p-4 text-left transition-all ${active === t.key ? "border-primary bg-primary/5 shadow-md" : "border-slate-200 bg-white hover:border-primary/40"}`}
              >
                <Icon className={`h-4 w-4 ${active === t.key ? "text-primary" : "text-slate-400"}`} />
                <div className="mt-2 font-display text-2xl font-black text-[#0C2136]">{stats?.[t.key] ?? "…"}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{t.label}</div>
              </button>
            );
          })}
        </div>

        {/* table */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-display font-bold text-lg text-[#0C2136]">
              {TABS.find((t) => t.key === active)?.label} <span className="text-sm text-slate-400 font-normal">({rows.length})</span>
            </h2>
            <button onClick={exportCsv} data-testid="admin-export-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}
            {error && <div className="p-8 text-center text-sm text-red-600">{error}</div>}
            {!loading && !error && (
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    {allCols.map((c) => (
                      <th key={c} className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/60">
                      {cols.map((c) => (
                        <td key={c} className="px-4 py-3 align-top whitespace-nowrap text-slate-700 max-w-[280px] truncate" title={cellValue(r, c)}>
                          {c === "overall" && r.overall != null ? (
                            <span className="font-bold" style={{ color: r.overall >= 65 ? "#3FA24B" : r.overall >= 45 ? "#E39114" : "#D24A34" }}>{r.overall}/100</span>
                          ) : cellValue(r, c)}
                        </td>
                      ))}
                      {showReportAction && (
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <button
                            onClick={() => setReportRow(r)}
                            data-testid={`admin-view-report-${i}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white px-3.5 py-1.5 text-xs font-bold transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" /> View Report
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={allCols.length} className="px-4 py-10 text-center text-slate-400">No entries yet</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {reportRow && (
        <ReportViewer row={reportRow} onClose={() => setReportRow(null)} />
      )}
    </div>
  );
}

function ReportViewer({ row, onClose }) {
  const report = useMemo(() => {
    try {
      if (!row.profile?.A1) return null;
      return computeReport(row.profile, row.answers || {});
    } catch {
      return null;
    }
  }, [row]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm overflow-y-auto" data-testid="admin-report-viewer">
      <div className="min-h-full py-6 px-3 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="print-hide sticky top-3 z-10 flex items-center justify-between rounded-2xl bg-[#0C2136] text-white px-5 py-3 shadow-xl mb-4">
            <div className="text-sm">
              <span className="font-display font-bold">Report</span>
              <span className="text-white/60 ml-2">{row.name} · {row.company}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                data-testid="admin-report-print-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-xs font-bold"
              >
                <Download className="h-3.5 w-3.5" /> Print / Save PDF
              </button>
              <button
                onClick={onClose}
                data-testid="admin-report-close-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-4 py-2 text-xs font-bold hover:bg-white/10"
              >
                <X className="h-3.5 w-3.5" /> Close
              </button>
            </div>
          </div>
          {report ? (
            <div className="tcc">
              <Report
                report={report}
                name={row.name}
                company={row.company}
                email={row.email}
                mobile={row.mobile}
                onRestart={onClose}
              />
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-10 text-center text-slate-500 text-sm">
              This entry was recorded before full report capture was enabled — no report data is available to render.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
